import {
  useCallback,
  useRef,
  type ComponentPropsWithoutRef,
  type JSX,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cx } from './internal/cx.js';
import { useFocusTrap } from './internal/useFocusTrap.js';

/** Dialog width: `default` for confirmations, `wide` for content-heavy views. */
export type ModalSize = 'default' | 'wide';

/** Where the dialog sits: `center` for confirmations, `right` for a full-height detail drawer. */
export type ModalPlacement = 'center' | 'right';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Accessible label when no visible title is provided. */
  'aria-label'?: string;
  /** ID of the element that labels the dialog. */
  'aria-labelledby'?: string;
  /**
   * `default` is the 28rem confirm-dialog width.
   * `wide` is for content-heavy dialogs that should use most of the viewport.
   */
  size?: ModalSize;
  /**
   * `right` anchors the panel to the trailing edge at full height — the drawer form, for detail
   * and usage panels opened from a list row. The drawer sets its own width, so `size` has no
   * effect on a right-placed panel.
   */
  placement?: ModalPlacement;
  className?: string;
}

export interface ModalFooterProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

function ModalFooter({ children, className, ...props }: ModalFooterProps): JSX.Element {
  return (
    <div className={cx('nrx-modal__footer', className)} {...props}>
      {children}
    </div>
  );
}

/** Accessible modal dialog with overlay, escape-to-close, focus trap, and scroll lock. */
export function Modal({
  open,
  onClose,
  children,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  size = 'default',
  placement = 'center',
  className,
}: ModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleEscape = useCallback((): void => {
    onCloseRef.current();
  }, []);

  useFocusTrap(dialogRef, {
    active: open,
    onEscape: handleEscape,
    restoreFocus: true,
    lockScroll: true,
  });

  if (!open) return null;

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cx('nrx-modal__overlay', placement === 'right' && 'nrx-modal__overlay--drawer')}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        className={cx(
          'nrx-modal__content',
          size === 'wide' && 'nrx-modal__content--wide',
          placement === 'right' && 'nrx-modal__content--drawer',
          className,
        )}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

Modal.Footer = ModalFooter;
